import { useEffect, useState } from 'react';
import { getAllSponsors, createSponsor, updateSponsor } from '../../services/apiClient';
import type { Sponsor, SponsorshipLevel } from '../../types';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Card } from '../../components/ui/Card';
import { SPONSORSHIP_LEVEL_LABELS, SPONSORSHIP_LEVEL_ORDER } from '../../lib/constants';
import styles from './ManageSponsors.module.css';

type FormState = {
  name: string;
  logo_url: string;
  website_url: string;
  sponsorship_level: SponsorshipLevel;
  display_order: number;
  active: boolean;
};

const EMPTY_FORM: FormState = {
  name: '',
  logo_url: '',
  website_url: '',
  sponsorship_level: 'community',
  display_order: 99,
  active: true,
};

export function ManageSponsors() {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = () => {
    getAllSponsors().then((r) => {
      if (r.data) setSponsors(r.data.sort((a, b) => a.display_order - b.display_order));
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (s: Sponsor) => {
    setForm({
      name: s.name,
      logo_url: s.logo_url,
      website_url: s.website_url,
      sponsorship_level: s.sponsorship_level,
      display_order: s.display_order,
      active: s.active,
    });
    setEditId(s.id);
    setErrors({});
    setShowForm(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    if (editId) {
      await updateSponsor(editId, form);
    } else {
      await createSponsor(form);
    }
    setSaving(false);
    setShowForm(false);
    load();
  };

  const toggleActive = async (s: Sponsor) => {
    await updateSponsor(s.id, { active: !s.active });
    load();
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <p className={styles.count}>{sponsors.length} sponsor{sponsors.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={openNew}>+ Add Sponsor</Button>
      </div>

      {showForm && (
        <Card padding="md">
          <h2 className={styles.formTitle}>{editId ? 'Edit Sponsor' : 'New Sponsor'}</h2>
          <form onSubmit={handleSubmit} noValidate className={styles.form}>
            <div className={styles.fields}>
              <div className={styles.field}>
                <label htmlFor="sp-name" className={styles.label}>Sponsor Name <span className={styles.req}>*</span></label>
                <input id="sp-name" name="name" type="text" value={form.name} onChange={handleChange}
                  className={[styles.input, errors.name ? styles.inputError : ''].join(' ')} />
                {errors.name && <p className={styles.error}>{errors.name}</p>}
              </div>
              <div className={styles.field}>
                <label htmlFor="sp-level" className={styles.label}>Sponsorship Level</label>
                <select id="sp-level" name="sponsorship_level" value={form.sponsorship_level} onChange={handleChange} className={styles.select}>
                  {SPONSORSHIP_LEVEL_ORDER.map((l) => (
                    <option key={l} value={l}>{SPONSORSHIP_LEVEL_LABELS[l]}</option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="sp-logo" className={styles.label}>Logo URL</label>
                <input id="sp-logo" name="logo_url" type="url" value={form.logo_url} onChange={handleChange} className={styles.input} placeholder="https://…" />
              </div>
              <div className={styles.field}>
                <label htmlFor="sp-web" className={styles.label}>Website URL</label>
                <input id="sp-web" name="website_url" type="url" value={form.website_url} onChange={handleChange} className={styles.input} placeholder="https://…" />
              </div>
              <div className={styles.field}>
                <label htmlFor="sp-order" className={styles.label}>Display Order</label>
                <input id="sp-order" name="display_order" type="number" min="1" value={form.display_order} onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" name="active" checked={form.active} onChange={handleChange} className={styles.checkbox} />
                  Active (visible on public site)
                </label>
              </div>
            </div>
            <div className={styles.formActions}>
              <Button type="submit" loading={saving}>{editId ? 'Save Changes' : 'Add Sponsor'}</Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : sponsors.length === 0 ? (
        <EmptyState title="No sponsors yet" description="Add sponsors to display them on the public site." />
      ) : (
        <div className={styles.list}>
          {sponsors.map((s) => (
            <div key={s.id} className={[styles.item, !s.active ? styles.inactive : ''].join(' ')}>
              <div className={styles.itemInfo}>
                <div className={styles.itemMeta}>
                  <span className={styles.level}>{SPONSORSHIP_LEVEL_LABELS[s.sponsorship_level]}</span>
                  <StatusBadge label={s.active ? 'active' : 'inactive'} />
                  <span className={styles.order}>#{s.display_order}</span>
                </div>
                <p className={styles.itemName}>{s.name}</p>
                {s.website_url && s.website_url !== '#' && (
                  <a href={s.website_url} target="_blank" rel="noopener noreferrer" className={styles.itemUrl}>{s.website_url}</a>
                )}
              </div>
              <div className={styles.itemActions}>
                <button className={styles.actionBtn} onClick={() => openEdit(s)}>Edit</button>
                <button className={styles.actionBtn} onClick={() => toggleActive(s)}>
                  {s.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
